import { formatDate } from '@angular/common';
import { Component, Inject, forwardRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { DateRange } from '@angular/material/datepicker';
import { TranslateModule } from '@ngx-translate/core';
import { Locale, addYears, format, parse } from 'date-fns';
import { distinctUntilChanged, filter, tap } from 'rxjs';
import { DateRangePickerPresetId, TmDateRangePickerComponent } from '../date-range-picker';
import { OutOfOffice } from './out-of-office.type';

@Component({
    selector: 'tm-out-of-office',
    templateUrl: './out-of-office.component.html',
    imports: [
        ReactiveFormsModule,
        TmDateRangePickerComponent,
        TranslateModule
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TmOutOfOfficeComponent),
            multi: true
        }
    ]
})
export class TmOutOfOfficeComponent implements ControlValueAccessor{
  private _value: OutOfOffice[] = [];
  protected isDisabled: boolean;

  protected get value() {
    return this._value;
  }

  protected set value(value) {
    if (value && value !== this.value) {
      this._value = value;
      this.onChange(value);
      this.onTouched();
    }
  }

  onChange: any = () => { };
  onTouched: any = () => { };

  protected dateRangeCtrl = new FormControl<DateRange<Date>>(null);

  protected presetIds = [
    DateRangePickerPresetId.tomorrow,
    DateRangePickerPresetId.nextWeek,
    DateRangePickerPresetId.next2Weeks,
  ];

  protected maxDate = addYears(new Date(), 1);

  constructor(
    @Inject(MAT_DATE_LOCALE) private dateLocale: Locale
  ) {
    this.dateRangeCtrl.valueChanges.pipe(
      distinctUntilChanged(),
      filter(dateRange => !!dateRange),
      tap(dateRange => {
        this.value = [
          ...this.value,
          { from: this.dateString(dateRange.start), to: this.dateString(dateRange.end) }
        ];
        this.dateRangeCtrl.setValue(null);
      }),
      takeUntilDestroyed()
    ).subscribe()
  }

  writeValue(value: OutOfOffice[]): void {
    this._value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  longFormat(dateStr: string) {
    const date = parse(dateStr, "dd/MM/yyyy", new Date());
    return  formatDate(date, 'longDate', this.dateLocale.code);
  }

  remove(index: number) {
    this.value = this.value.filter((_, i) => i !== index);
  }

  private dateString(date: Date) {
    if (!date) return '';
    
    return format(date, "dd/MM/yyyy", { locale: this.dateLocale });
  }

}